import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Plus, Trash, Power, Buildings, UserCircle, Users } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import { formatDate } from '../utils/dateFormat';
import { TenantMigrationCard } from '@/components/TenantMigrationCard';

export default function WorkspacesPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', admin_name: '', admin_email: '', admin_password: '' });

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await api.get('/tenants');
      setTenants(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška pri učitavanju');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const submit = async () => {
    if (!form.name || !form.admin_name || !form.admin_email || !form.admin_password) {
      toast.error('Popunite sva polja');
      return;
    }
    if (form.admin_password.length < 6) {
      toast.error('Lozinka minimum 6 karaktera');
      return;
    }
    try {
      await api.post('/tenants', form);
      toast.success('Workspace kreiran');
      setOpen(false);
      setForm({ name: '', admin_name: '', admin_email: '', admin_password: '' });
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška');
    }
  };

  const toggle = async (t) => {
    try {
      await api.patch(`/tenants/${t.id}`, { active: !t.active });
      toast.success(t.active ? 'Workspace deaktiviran' : 'Workspace aktiviran');
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška');
    }
  };

  const del = async (t) => {
    if (!(await confirmDialog({
      title: 'Obrisati workspace?',
      description: `Ovo će nepovratno obrisati SVE podatke (${t.clients} klijenata, ${t.users} korisnika) workspace-a "${t.name}". Sigurni ste?`,
      confirmText: 'Obriši zauvijek',
      variant: 'danger',
    }))) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success('Workspace obrisan');
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška');
    }
  };

  return (
    <div className="space-y-6" data-testid="workspaces-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Agencije / Workspace-i</h3>
          <p className="text-sm text-slate-600 mt-1">Upravljanje odvojenim workspace-ovima za različite agencije. Svaki workspace ima potpuno odvojenu bazu podataka.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="workspace-add-btn">
          <Plus size={20} weight="bold" className="mr-2" /> Novi workspace
        </Button>
      </div>

      <TenantMigrationCard onDone={fetch} />

      {loading ? (
        <p className="text-center text-slate-500 py-16">Učitavanje...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map(t => (
            <Card key={t.id} className={`p-5 ${!t.active ? 'opacity-60 border-red-200' : ''}`} data-testid={`workspace-card-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Buildings size={22} weight="duotone" className={t.is_default ? 'text-blue-600' : 'text-slate-600'} />
                  <h4 className="font-semibold text-slate-900">{t.name}</h4>
                </div>
                {t.is_default ? (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">GLAVNI</span>
                ) : (
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${t.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {t.active ? 'AKTIVAN' : 'DEAKTIVIRAN'}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 space-y-1 mb-4">
                <div className="flex items-center gap-2"><Users size={14} /> {t.users} korisnika</div>
                <div className="flex items-center gap-2"><UserCircle size={14} /> {t.clients} klijenata</div>
                {t.created_at && <div>Kreirano: {formatDate(t.created_at)}</div>}
              </div>
              {!t.is_default && (
                <div className="flex gap-2 pt-3 border-t border-slate-100">
                  <button
                    onClick={() => toggle(t)}
                    className={`flex-1 text-xs py-1.5 rounded flex items-center justify-center gap-1 font-medium transition ${t.active ? 'bg-red-50 hover:bg-red-100 text-red-700' : 'bg-green-50 hover:bg-green-100 text-green-700'}`}
                    data-testid={`workspace-toggle-${t.id}`}
                  >
                    <Power size={14} />
                    {t.active ? 'Deaktiviraj' : 'Aktiviraj'}
                  </button>
                  <button
                    onClick={() => del(t)}
                    className="px-3 py-1.5 rounded bg-slate-100 hover:bg-red-100 text-slate-600 hover:text-red-700 transition"
                    title="Obriši zauvijek"
                    data-testid={`workspace-delete-${t.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Novi workspace</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-xs text-slate-500">
              Kreiranje novog workspace-a automatski pravi odvojenu bazu podataka i administratorski nalog.
              Taj admin se loguje istim login formama, ali radi na potpuno odvojenim podacima.
            </p>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600 uppercase font-semibold">Naziv agencije / workspace-a *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="npr. DRUGAA AGENCIJA DOO" className="bg-white border-slate-300" data-testid="workspace-name-input" />
            </div>
            <div className="border-t pt-3 space-y-3">
              <p className="text-xs font-semibold text-slate-700">Admin nalog za workspace:</p>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600 uppercase font-semibold">Ime i prezime *</Label>
                <Input value={form.admin_name} onChange={e => setForm({...form, admin_name: e.target.value})} className="bg-white border-slate-300" data-testid="workspace-admin-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600 uppercase font-semibold">Email *</Label>
                <Input type="email" value={form.admin_email} onChange={e => setForm({...form, admin_email: e.target.value})} className="bg-white border-slate-300" data-testid="workspace-admin-email-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600 uppercase font-semibold">Privremena lozinka *</Label>
                <Input type="text" value={form.admin_password} onChange={e => setForm({...form, admin_password: e.target.value})} placeholder="min 6 karaktera" className="bg-white border-slate-300 font-mono" data-testid="workspace-admin-password-input" />
                <p className="text-xs text-slate-400">Saopšti ovu lozinku adminu — trebaće je promijeniti pri prvom loginu</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setOpen(false)}>Odustani</Button>
              <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="workspace-submit-btn">Kreiraj workspace</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
